import type { BoundedTotal } from './paging';
import type { HubApplicationStatus } from './hubRegistration';
import type { OrderStatus } from './orders';
import type { StatisticsSummary } from './statistics';
import type { Role } from './common';
import type { SeverityCounts } from './overview';

interface ReaderPair { online: number; total: number }
interface Stock { full: number; empty: number; total: number }

/** What a Petugas sees today at its location, by the location's type (GET /home › operator). */
export type OperatorDay =
  | { type: 'OWNER_WAREHOUSE'; registeredToday: number; departedToday: number; stock: Stock; readers: ReaderPair }
  | { type: 'FILLING_STATION'; incomingEmpty: number; departedFullToday: number; receivedToday: number; stock: Stock; withdrawalsDue: number; readers: ReaderPair }
  | {
      type: 'HUB';
      queue: Partial<Record<OrderStatus, number>>;
      slaBreached: number;
      dueSoon: number;
      completedToday: number;
      verificationBacklog: number;
      stock: { full: number; empty: number; reservedFull: number; freeFull: number; withCourier: number };
      couriers: { active: number; out: number };
      emptiesExpected: number;
      emptiesOverdue: number;
      dormantHeld: number;
      readers: ReaderPair;
    }
  | { type: 'TEST_WORKSHOP'; incomingWithdrawals: number; toTest: number; passedToday: number; failedToday: number; returnsDepartedToday: number; readers: ReaderPair };

/** GET /home: the role's blocks (absent blocks are omitted). */
export interface Home {
  role: Role;
  scope: { kind: 'national' | 'province' | 'location'; provinceCode: string | null; locationId: number | null; locationType: string | null };
  asOf: string;
  actions?: {
    alerts: SeverityCounts;
    hubApplications: { byStage: Partial<Record<HubApplicationStatus, number>>; overdue: number };
    householdsPending: BoundedTotal;
    slaBreaches: number;
    finesToReview: number;
  };
  network?: { readers: { online: number; offline: number; total: number }; /** national admins only: the version being rolled out, active rollouts, failed gateways */
  ota: { version: string | null; rolloutsActive: number; failed: number } | null };
  /** null for a caller without pii.read in a small area (suppression) */
  sparkline?: { days: { day: string; exchanges: number; departed: number; received: number }[] } | null;
  statistics?: StatisticsSummary;
  operator?: OperatorDay;
  courier?: { assigned: number; out: number; failedToday: number; completedToday: number };
  verifier?: { today: number; upcoming: number; overdue: number; doneThisMonth: number };
}

export const homePaths = { home: '/home' };
